import { Progress } from "@/components/ui/progress";
import { formatBytes } from "@/lib/admin/utils/format-bytes";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function usagePercent(used: number | null, limit: number | null): number | null {
  if (used == null || limit == null || limit === 0) return null;
  return Math.min(Math.round((used / limit) * 100), 100);
}

function progressColor(pct: number | null): string {
  if (pct == null) return "bg-gray-300";
  if (pct > 90) return "bg-red-500";
  if (pct > 70) return "bg-yellow-400";
  return "bg-green-500";
}

export interface UsageBarProps {
  label: string;
  used: number | null;
  limit: number | null;
  /** Default: formatBytes. Le quote non-byte (MAU, invocazioni) passano il proprio formatter. */
  formatValue?: (v: number | null) => string;
}

export function UsageBar({ label, used, limit, formatValue = formatBytes }: UsageBarProps) {
  const pct = usagePercent(used, limit);
  const barColor = progressColor(pct);
  const usedStr = formatValue(used);
  const limitStr = formatValue(limit);
  const pctStr = pct != null ? `${pct}%` : "—";

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-500 text-xs">
          {usedStr} / {limitStr} ({pctStr})
        </span>
      </div>
      {pct != null ? (
        <Progress
          value={pct}
          className="h-2 bg-gray-100"
          style={
            {
              "--progress-indicator-color": undefined,
            } as React.CSSProperties
          }
        >
          {/* Override indicator color via inline wrapper since Progress uses bg-primary */}
        </Progress>
      ) : (
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div className="h-full w-0 rounded-full bg-gray-300" />
        </div>
      )}
      {/* Colored bar overlay using Tailwind classes */}
      {pct != null && (
        <div className="relative -mt-2 h-2 w-full overflow-hidden rounded-full">
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default UsageBar;
